import dayjs from 'dayjs';
import * as echarts from 'echarts';
import { keepTwoNull } from '@/utils/utils';
import { TimeDataItem } from '../../utils';

const areaColors = [
  new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: 'rgba(45,141,255,0.1)' },
    { offset: 1, color: 'rgba(45,141,255,0)' },
  ]),
  new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: 'rgba(22,221,142,0.1)' },
    { offset: 1, color: 'rgba(22,221,142,0)' },
  ]),
];

export const getChartOption = (data: TimeDataItem[]) => {
  let AData = (data ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.voltageAb)]);
  let BData = (data ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.voltageBc)]);
  let CData = (data ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.voltageCa)]);

  let yAxisTotalData = AData.concat(BData, CData)
    .filter(item => item[1] !== '--')
    .map(i => Number(i[1]));

  return {
    tooltip: {
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue).format('YYYY-MM-DD HH:mm:ss');
        let otherStr = '';
        let str = '';

        items.forEach((item: any) => {
          const { seriesName, data, marker } = item;
          otherStr += `<div style="margin-top:8px"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}</div><div>${
            data[1] || data[1] === 0 ? data[1] : '-'
          }V</div></div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },
    grid: {
      left: 10,
      right: 10,
      top: 50,
      bottom: 20,
      containLabel: true,
    },
    legend: {
      right: 0,
      top: 10,
      bottom: 0,
      icon: 'rect',
      itemWidth: 16,
      itemHeight: 4,
      itemGap: 24,
      padding: [0, 8, 0, 0],
    },
    xAxis: [
      {
        type: 'time',
        axisLabel: {
          formatter: function (e: number) {
            return dayjs(e, 'x').format('HH:mm');
          },
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: 'V',
        axisLabel: { formatter: (value: number) => value.toFixed(2) },
        min: yAxisTotalData.length > 0 ? Math.floor(Math.min(...yAxisTotalData)) : 0,
        max: yAxisTotalData.length > 0 ? Math.ceil(Math.max(...yAxisTotalData)) : 0,
      },
    ],
    series: [
      {
        name: 'AB线电压',
        type: 'line',
        data: AData,
        connectNulls: true,
        smooth: true,
        showSymbol: false,
        itemStyle: {
          color: '#FFCB47',
        },
      },
      {
        name: 'BC线电压',
        type: 'line',
        data: BData,
        connectNulls: true,
        smooth: true,
        showSymbol: false,
        itemStyle: {
          color: '#59D744',
        },
      },
      {
        name: 'CA线电压',
        type: 'line',
        showSymbol: false,
        data: CData,
        connectNulls: true,
        smooth: true,
        itemStyle: {
          color: '#FF4D4F',
        },
      },
    ],
  };
};

export const getPowerChartOption = (data: TimeDataItem[]) => {
  let powerData = (data ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.power)]);
  let loadRateData = (data ?? []).map(item => [dayjs(item.time).valueOf(), keepTwoNull(item.loadRate)]);

  const powerValue = powerData.filter(item => item[1] !== '--').map(i => Number(i[1]));
  const minValue = powerValue.length > 0 ? Math.min(...powerValue) : 0;
  const maxValue = powerValue.length > 0 ? Math.max(...powerValue) : 0;
  return {
    tooltip: {
      formatter: (items: any) => {
        let { axisValue } = items[0];
        let firstStr = dayjs(axisValue).format('YYYY-MM-DD HH:mm');
        let otherStr = '';
        let str = '';

        items.forEach((item: any) => {
          const { seriesName, data, marker, seriesIndex } = item;
          otherStr += `<div style="margin-top:8px"><div style="display:flex;justify-content:space-between;align-items:center;"><div><span>${marker}</span>${seriesName}</div><div>${
            data[1] || data[1] === 0 ? data[1] : '-'
          }${seriesIndex === 0 ? 'kW' : '%'}</div></div>`;
          str = firstStr + otherStr;
        });

        return str;
      },
    },
    grid: {
      left: 10,
      right: 10,
      top: 50,
      bottom: 20,
      containLabel: true,
    },
    legend: {
      right: 0,
      top: 10,
      bottom: 0,
      icon: 'rect',
      itemWidth: 16,
      itemHeight: 4,
      itemGap: 24,
      padding: [0, 8, 0, 0],
    },
    xAxis: [
      {
        type: 'time',
        axisLabel: {
          formatter: function (e: number) {
            return dayjs(e, 'x').format('HH:mm');
          },
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: 'kW',
        alignTicks: true,
        axisLabel: { formatter: (value: number) => value.toFixed(2) },
        min: Math.floor(minValue),
        max: Math.ceil(maxValue),
      },
      {
        type: 'value',
        name: '%',
        axisLabel: {
          formatter: '{value}',
        },
      },
    ],
    series: [
      {
        name: '功率',
        type: 'line',
        data: powerData,
        connectNulls: true,
        smooth: true,
        showSymbol: false,
        itemStyle: {
          color: '#16DD8E',
        },
        areaStyle: {
          color: areaColors[0],
        },
        yAxisIndex: 0,
      },
      {
        name: '负载率',
        type: 'line',
        data: loadRateData,
        connectNulls: true,
        smooth: true,
        showSymbol: false,
        itemStyle: {
          color: '#2D8DFF',
        },
        areaStyle: {
          color: areaColors[1],
        },
        yAxisIndex: 1,
      },
    ],
  };
};
